import type {
  GenerateOutlineResult,
  OutlineItem,
  SlideLayoutMapping,
} from "../types/generation";
import type { Slide } from "../components/sortable-slide";

/**
 * Rebuilds the docpilot outline from the user-edited slide list, preserving
 * the original /generate-outline metadata (tone, image guidelines, title) and
 * each surviving slide's dataPoints. Falls back to synthesized defaults when
 * no metadata is available (restored pre-upgrade drafts, template Create-with-AI
 * paths that never ran /generate-outline).
 */
export const buildDeckOutline = (input: {
  slides: Slide[];
  outlineMeta: GenerateOutlineResult | null;
  docType: string;
  companyName: string | null;
}): GenerateOutlineResult => {
  const originalByName = new Map<string, OutlineItem>(
    (input.outlineMeta?.slides ?? []).map((slide) => [
      slide.internalName,
      slide,
    ]),
  );
  const slides: OutlineItem[] = input.slides.map((slide) => {
    const original = slide.internalName
      ? originalByName.get(slide.internalName)
      : undefined;
    return {
      internalName: slide.internalName || slide.id,
      title: slide.title,
      // A slide the user added but never described carries an empty string;
      // sending `[""]` asks the model to write a slide about nothing, so send
      // no content at all and let it work from the title.
      content: slide.description.trim() ? [slide.description] : [],
      dataPoints: original?.dataPoints ?? [],
    };
  });
  return {
    storyTitle:
      input.outlineMeta?.storyTitle ||
      (input.companyName
        ? `${input.companyName} ${input.docType}`
        : input.docType),
    slides,
    toneOfVoice:
      input.outlineMeta?.toneOfVoice || "Professional, confident, and clear",
    currentdate: input.outlineMeta?.currentdate || new Date().toISOString(),
    imageGuidelines:
      input.outlineMeta?.imageGuidelines ||
      "Use professional, on-brand, realistic imagery.",
  };
};

/**
 * Pairs each outline slide with its layout mapping by internalName, walking
 * the outline so the deck keeps the user-approved slide order even when the
 * layouts call echoes its mappings shuffled — and so a dropped slide can't
 * shift designs onto the wrong content, which is what positional zipping did.
 * When the backend echoes unrecognizable internalNames (no intersection at
 * all), falls back to the old positional pairing rather than generating nothing.
 */
export const pairMappingsWithOutline = (
  mappings: SlideLayoutMapping[],
  outline: GenerateOutlineResult,
): { mapping: SlideLayoutMapping; slide: OutlineItem }[] => {
  const byName = new Map(
    mappings.map((mapping) => [mapping.internalName, mapping]),
  );
  const matched = outline.slides.flatMap((slide) => {
    const mapping = byName.get(slide.internalName);
    return mapping ? [{ mapping, slide }] : [];
  });
  if (matched.length > 0) return matched;
  return mappings
    .slice(0, outline.slides.length)
    .map((mapping, index) => ({ mapping, slide: outline.slides[index] }));
};
